import { CommonModule, NgFor, NgIf } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CustomValidatorsService } from '@AppCore/services/custom-validators.service';
import { GlobalUtilityService } from '@AppCore/services/global-utility.service';
import { UserService } from '@AppRoot/features-modules/user/user.service';
import { APIResponse } from '@AppRoot/shared/models/api-response.interface';
import { Address } from '@AppRoot/shared/models/user.interface';
import { SharedService } from '@AppRoot/shared/services/shared.service';
import { DropdownModule } from 'primeng/dropdown';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  of,
  Subject,
  switchMap,
  takeUntil,
  throwError,
  tap,
  Subscription,
} from 'rxjs';

@Component({
  selector: 'app-address-form',
  templateUrl: './address-form.component.html',
  styleUrls: ['./address-form.component.scss'],
  standalone: true,
  imports: [NgIf, NgFor, CommonModule, DropdownModule, ReactiveFormsModule],
})
export class AddressFormComponent implements OnInit, OnDestroy {
  states: Array<{ id: number; name: string }> = [];
  cities: Array<{ id: number; name: string; state_id: number }> = [];
  selectedState: any;
  selectedCity: any;
  showPinCodeSuccess: any;
  loading = false;
  isPincodeValidating = false;

  private destroy$ = new Subject<void>();
  private pincodeSubscription: Subscription | null = null;

  @Output() closeAddressForm = new EventEmitter<void>();
  @Output() updatedAddress = new EventEmitter<any>();
  @Input() minimal = false;

  @Input()
  set selectedAddress(value: Address | null) {
    if (value) {
      this.addressForm.patchValue({
        ...value,
      });
      this.loadStates(value.state, value.city);
    }
  }

  @Input()
  set submitAddress(value: Boolean) {
    if (value) {
      this.saveAddress();
    }
  }

  addressForm: FormGroup<any> = new FormGroup({
    id: new FormControl(''),
    apartment_no: new FormControl(''),
    address_line: new FormControl('', [Validators.required]),
    city: new FormControl('', [Validators.required]),
    state: new FormControl('', [Validators.required]),
    country: new FormControl('India', [Validators.required]),
    pincode: new FormControl(null, [
      Validators.required,
      Validators.minLength(6),
      Validators.maxLength(6),
      Validators.pattern('^[0-9]*$'),
    ]),
    phone: new FormControl('', [
      Validators.required,
      CustomValidatorsService.phoneValidator,
    ]),
    email: new FormControl('', [Validators.required, Validators.email]),
    is_default: new FormControl(0),
    receiver_name: new FormControl('', [
      Validators.required,
      CustomValidatorsService.text,
    ]),
  });

  constructor(
    private _userService: UserService,
    private _globalutilityService: GlobalUtilityService,
    private _sharedService: SharedService
  ) {
    this.loadStates();
  }

  ngOnInit(): void {
    this.setupPincodeValidation();
  }

  closeForm() {
    this.closeAddressForm.emit();
  }

  private setupPincodeValidation() {
    // Unsubscribe from previous subscription if exists
    this.pincodeSubscription?.unsubscribe();

    this.pincodeSubscription = this.addressForm
      .get('pincode')!
      .valueChanges.pipe(
        // Reset states immediately on any change
        tap(() => {
          this.showPinCodeSuccess = false;
          this.isPincodeValidating = false;
          // Clear city and state when pincode changes
          this.addressForm.patchValue(
            {
              city: '',
              state: '',
            },
            { emitEvent: false }
          );
          this.cities = [];
        }),
        debounceTime(300), // Increased debounce time
        filter((pin: string | null): pin is string => {
          if (!pin) return false;

          // Check if pincode is valid format
          if (!/^\d{6}$/.test(pin)) {
            if (pin.length > 0) {
              this.addressForm.get('pincode')?.setErrors({
                invalid: true,
                message: 'Pincode must be 6 digits',
              });
            }
            return false;
          }

          // Clear previous errors if format is valid
          const currentErrors = this.addressForm.get('pincode')?.errors;
          if (
            currentErrors &&
            (currentErrors['invalid'] || currentErrors['notServiceable'])
          ) {
            this.addressForm.get('pincode')?.setErrors(null);
          }

          return true;
        }),
        tap(() => {
          this.isPincodeValidating = true;
          // this._globalutilityService.showLoader();
        }),
        switchMap((pin: string) =>
          this._sharedService.checkPincodeServiceability(pin).pipe(
            catchError((error) => {
              console.error('Pincode serviceability check failed:', error);
              this.addressForm.get('pincode')!.setErrors({
                invalid: true,
                message: 'Unable to verify pincode',
              });
              return throwError(() => error);
            })
          )
        ),
        switchMap((res: APIResponse) => {
          if (!res.data?.delivery?.deliverable) {
            this.addressForm.get('pincode')!.setErrors({
              notServiceable: true,
              message: 'Sorry, we do not deliver to this pincode',
            });
            return throwError(() => new Error('Not serviceable'));
          }
          return of(res);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (response: APIResponse) => {
          this.handleSuccessfulPincodeValidation(response);
        },
        error: (error) => {
          this.handlePincodeValidationError(error);
        },
      });
  }

  private handleSuccessfulPincodeValidation(response: APIResponse) {
    const data = response.data.pincodeData.data;
    this.isPincodeValidating = false;
    this.loading = false;
    this.showPinCodeSuccess = true;
    this._globalutilityService.hideLoader();

    // Find and patch state
    const matchedState = this.states.find(
      (s) => s.name.toLowerCase() === data.state.toLowerCase()
    );

    if (matchedState) {
      this.addressForm.patchValue(
        {
          state: matchedState.name,
        },
        { emitEvent: false }
      );

      // Load cities and preselect the city
      this.getCities(matchedState.id, data.district);
    } else {
      console.warn('State from PIN not found in states list:', data.state);
      // You might want to handle this case - perhaps add the state to the list
      // or show a message to the user
    }
  }

  private handlePincodeValidationError(error: any) {
    this.isPincodeValidating = false;
    this.loading = false;
    this.showPinCodeSuccess = false;
    this._globalutilityService.hideLoader();

    // Error is already set in the switchMap catchError
    console.error('Pincode validation error:', error);
  }

  private getCities(state_id: number, preselectCity?: string) {
    this.loading = true;
    this.addressForm.patchValue(
      {
        city: preselectCity,
      },
      { emitEvent: false }
    );
    // this._sharedService.getCities(state_id).subscribe({
    //   next: (res: APIResponse) => {
    //     this.loading = false;
    //     this.cities = res.data;

    //     if (preselectCity) {
    //       const cityObj = res.data.find((c: any) => {
    //         return c.name.toLowerCase() === preselectCity.toLowerCase();
    //       });

    //       if (cityObj) {
    //         this.addressForm.patchValue(
    //           {
    //             city: cityObj.name,
    //           },
    //           { emitEvent: false }
    //         );
    //       }
    //     }
    //   },
    //   error: (err) => {
    //     this.loading = false;
    //     console.error('Error fetching cities:', err);
    //     this._globalutilityService.showToast('Error', 'Failed to load cities');
    //   },
    // });
  }

  loadStates(state?: string, city?: string) {
    this._sharedService.getStates().subscribe({
      next: (res: APIResponse) => {
        this.loading = false;
        this._globalutilityService.hideLoader();
        this.states = res.data;

        if (state) {
          const selectedState = res.data.find((s: any) => s.name === state);
          if (selectedState) {
            this.addressForm.patchValue(
              {
                state: selectedState.name,
              },
              { emitEvent: false }
            );
            this.getCities(selectedState.id, city);
          }
        }
      },
      error: (error) => {
        console.error('Error loading states:', error);
        this._globalutilityService.showToast('Error', 'Failed to load states');
      },
    });
  }

  // Method to manually trigger pincode validation
  validatePincode() {
    const pincodeValue = this.addressForm.get('pincode')?.value;
    if (pincodeValue && /^\d{6}$/.test(pincodeValue)) {
      // Trigger the validation by emitting the current value
      this.addressForm.get('pincode')?.setValue(pincodeValue);
    }
  }

  updatePincode() {
    this.addressForm.patchValue({
      pincode: '',
      city: '',
      state: '',
    });
    this.showPinCodeSuccess = false;
    this.cities = [];
  }

  onStateChange(event: any) {
    const stateId = event.value.id;
    this.getCities(stateId);
    this.addressForm.patchValue({
      city: null,
      pincode: null,
    });
  }

  saveAddress() {
    if (!this.addressForm.valid) {
      this._globalutilityService.showToast(
        'Error',
        'Please fill all the required details correctly'
      );
      return;
    }

    const add = {
      ...this.addressForm.value,
      city: this.addressForm.value.city,
      state: this.addressForm.value.state,
    };

    if (!this._globalutilityService.isUserLoggedIn()) {
      this.updatedAddress.emit([add]);
      this.closeForm();
      return;
    }

    this._userService.updateAddress(add).subscribe({
      next: (response: APIResponse) => {
        this._globalutilityService.showToast(
          'success',
          'Address Updated Successfully'
        );
        this.updatedAddress.emit(response.data.addresses);
        this.closeForm();
      },
      error: (error: any) => {
        this._globalutilityService.showToast('error', 'Address Update Failed');
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.pincodeSubscription?.unsubscribe();
  }

  // Method to reinitialize validation if needed
  reinitializePincodeValidation() {
    this.setupPincodeValidation();
  }
}
